import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader, Panel, Select } from "@/components/admin/ui";
import { RestockList } from "./RestockList";

const emptyLine = () => ({ name: "", quantity: 1, unit: "case", notes: "" });

export function RestockRequestDialog({ sid, open, onOpenChange, onCreated }) {
  const [lines, setLines] = useState([emptyLine()]);
  const [meta, setMeta] = useState({ priority: "normal", notes: "", needed_by: "" });
  const [inv, setInv] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) api.get(`/inventory?store_id=${sid}`).then((r) => setInv(r.data)).catch(() => {}); }, [sid, open]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    const items = lines.filter((l) => l.name.trim()).map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 1 }));
    if (!items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      await api.post(`/stores/${sid}/restock-orders`, { items, priority: meta.priority, notes: meta.notes, needed_by: meta.needed_by || null });
      toast.success("Restock request submitted — admin has been emailed");
      setLines([emptyLine()]); setMeta({ priority: "normal", notes: "", needed_by: "" });
      onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Restock Request</DialogTitle></DialogHeader>
        <datalist id="inv-names">{inv.map((i) => <option key={i.id} value={i.name}/>)}</datalist>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_80px_90px_1fr_32px] gap-2 text-[11px] uppercase tracking-wider text-neutral-500 px-1"><span>Item</span><span>Qty</span><span>Unit</span><span>Notes</span><span/></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_90px_1fr_32px] gap-2 items-center" data-testid={`restock-line-${i}`}>
              <Input list="inv-names" value={l.name} onChange={(e) => { const it = inv.find((x) => x.name === e.target.value); setLine(i, { name: e.target.value, ...(it?.unit ? { unit: it.unit } : {}) }); }} placeholder="Salmon" data-testid={`restock-line-name-${i}`}/>
              <Input type="number" step="any" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`restock-line-qty-${i}`}/>
              <Input value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} placeholder="cases"/>
              <Input value={l.notes} onChange={(e) => setLine(i, { notes: e.target.value })} placeholder="optional"/>
              <button onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} className="rounded-lg" data-testid="restock-add-line"><Plus className="w-3 h-3 mr-1"/>Add item</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div><Label>Priority</Label>
            <Select value={meta.priority} onChange={(e) => setMeta({ ...meta, priority: e.target.value })} className="w-full" data-testid="restock-priority">
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
            </Select></div>
          <div><Label>Requested delivery date</Label><Input type="date" value={meta.needed_by} onChange={(e) => setMeta({ ...meta, needed_by: e.target.value })} data-testid="restock-needed-by"/></div>
        </div>
        <div><Label>Notes</Label><Textarea rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} data-testid="restock-notes"/></div>
        <Button onClick={submit} disabled={saving} className="w-full rounded-xl bg-[hsl(var(--primary))] h-11" data-testid="restock-submit">{saving ? "Submitting…" : "Submit Restock Request"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function StoreRestock() {
  const { sid } = useParams();
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/stores/${sid}/restock-orders${statusFilter ? `?status=${statusFilter}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [sid, statusFilter]);
  useEffect(load, [load]);

  const canCreate = isSuper || ["create", "edit", "approve", "manage"].includes(perms.restock);
  const canApprove = isSuper || ["approve", "manage"].includes(perms.restock);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Store · Supplies" title="Restock Requests" testId="restock-title"
        subtitle={`${items.length} request${items.length !== 1 ? "s" : ""}`}
        actions={<>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="restock-status-filter">
            <option value="">All statuses</option>
            {["new", "reviewed", "approved", "ordered", "delivered", "completed"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </Select>
          {canCreate && <Button onClick={() => setOpen(true)} className="rounded-xl bg-[hsl(var(--primary))] h-11 px-5" data-testid="add-restock"><Plus className="w-4 h-4 mr-1"/>Request Restock</Button>}
        </>}
      />
      <RestockRequestDialog sid={sid} open={open} onOpenChange={setOpen} onCreated={load}/>
      <Panel className="overflow-hidden"><RestockList items={items} canApprove={canApprove} onChange={load} showStore={false}/></Panel>
    </div>
  );
}
